// Pai Gow hand evaluator with correct Joker rules
// Categories:
// 0 High Card
// 1 Pair
// 2 Two Pair
// 3 Three of a Kind
// 4 Straight
// 5 Flush
// 6 Full House
// 7 Four of a Kind
// 8 Straight Flush
// 9 Royal Flush

const cardRanks = "23456789TJQKA"; // index 0..12

function getCardValue(card) {
  if (card === "JOKER") return -1; // special
  const rank = card.length === 3 ? "T" : card[0];
  return cardRanks.indexOf(rank);
}

function getCardSuit(card) {
  if (card === "JOKER") return null;
  return card[card.length - 1]; // last char = suit
}

function countRanks(values) {
  const counts = {};
  for (const v of values) counts[v] = (counts[v] || 0) + 1;
  return counts;
}

/** ---------- Straight helpers ---------- **/

function naturalStraightHigh(values) {
  const u = [...new Set(values)].sort((a, b) => a - b);
  for (let i = 0; i <= u.length - 5; i++) {
    if (u[i + 4] - u[i] === 4) return u[i + 4];
  }
  // A-2-3-4-5
  const hasWheel =
    u.includes(12) && u.includes(3) && u.includes(2) && u.includes(1) && u.includes(0);
  return hasWheel ? 3 : null;
}

function jokerStraightHigh(values) {
  const u = [...new Set(values)].sort((a, b) => a - b);
  for (let start = 0; start <= cardRanks.length - 5; start++) {
    const segment = [0, 1, 2, 3, 4].map(d => start + d);
    const missing = segment.filter(v => !u.includes(v));
    if (missing.length === 1) return segment[4];
  }
  const wheelSet = new Set([12, 0, 1, 2, 3]);
  let present = 0;
  for (const v of new Set(values)) if (wheelSet.has(v)) present++;
  if (present >= 4) return 3;
  return null;
}

/** ---------- Flush helpers ---------- **/

function naturalFlushSuit(suits) {
  const counts = {};
  for (const s of suits) {
    counts[s] = (counts[s] || 0) + 1;
    if (counts[s] >= 5) return s;
  }
  return null;
}

function jokerFlushSuit(suits) {
  const counts = {};
  for (const s of suits) counts[s] = (counts[s] || 0) + 1;
  for (const s of Object.keys(counts)) {
    if (s !== "null" && counts[s] === 4) return s;
  }
  return null;
}

/** ---------- Straight Flush helpers ---------- **/

function naturalStraightFlushHigh(cards, suit) {
  const suitedVals = cards
    .filter(c => getCardSuit(c) === suit)
    .map(getCardValue)
    .filter(v => v >= 0);
  return naturalStraightHigh(suitedVals);
}

function jokerStraightFlushHigh(cards, suit) {
  const suitedVals = cards
    .filter(c => c !== "JOKER" && getCardSuit(c) === suit)
    .map(getCardValue);
  const u = [...new Set(suitedVals)].sort((a, b) => a - b);

  for (let start = 0; start <= cardRanks.length - 5; start++) {
    const segment = [0, 1, 2, 3, 4].map(d => start + d);
    const missing = segment.filter(v => !u.includes(v));
    if (missing.length === 1) return segment[4];
  }

  const wheel = new Set([12, 0, 1, 2, 3]);
  let present = 0;
  for (const v of new Set(suitedVals)) if (wheel.has(v)) present++;
  if (present >= 4) return 3;

  return null;
}

/** ---------- Public evaluators ---------- **/

// ✅ FIXED: Low hand evaluation with Joker treated as pair
function evaluateLowHand(hand) {
  const [v1, v2] = hand.map(getCardValue);
  const ACE = cardRanks.indexOf("A");

  // Natural pair (no Joker)
  if (v1 !== -1 && v1 === v2) {
    return { category: 1, value: v1 };
  }

  // One Joker present
  if (v1 === -1 || v2 === -1) {
    const other = v1 === -1 ? v2 : v1;

    // Joker counts as Ace; if the other card is Ace -> Pair of Aces
    if (other === ACE) {
      return { category: 1, value: ACE };
    }

    // Otherwise just Ace-high
    return { category: 0, value: ACE };
  }

  // No Joker, no pair -> high card
  return { category: 0, value: Math.max(v1, v2) };
}


// High hand (5 cards) with Pai Gow Joker rules
function evaluateHighHand(hand) {
  const hasJoker = hand.includes("JOKER");
  const naturalCards = hand.filter(c => c !== "JOKER");
  const values = naturalCards.map(getCardValue).filter(v => v >= 0).sort((a, b) => b - a);
  const suits = naturalCards.map(getCardSuit);

  // ---- Straight Flush / Royal ----
  const natFlushSuit = naturalFlushSuit(suits);
  if (natFlushSuit) {
    const sfHigh = naturalStraightFlushHigh(naturalCards, natFlushSuit);
    if (sfHigh !== null) {
      if (isRoyalHigh(sfHigh)) return { category: 9, value: sfHigh };
      return { category: 8, value: sfHigh };
    }
  }
  if (hasJoker) {
    const suitCounts = countSuits(suits);
    const candidateSuits = Object.keys(suitCounts).sort((a, b) => suitCounts[b] - suitCounts[a]);
    for (const s of candidateSuits) {
      const sfHighJ = jokerStraightFlushHigh(hand, s);
      if (sfHighJ !== null) {
        if (isRoyalHigh(sfHighJ)) return { category: 9, value: sfHighJ };
        return { category: 8, value: sfHighJ };
      }
    }
  }

  // ---- Multiples ----
  const counts = countRanks(values);
  const countArr = Object.entries(counts)
    .map(([v, count]) => ({ value: +v, count }))
    .sort((a, b) => b.count - a.count || b.value - a.value);

  if (countArr[0]?.count === 4) return { category: 7, value: countArr[0].value };
  if (countArr[0]?.count === 3 && countArr[1]?.count === 2)
    return { category: 6, value: countArr[0].value };
  if (countArr[0]?.count === 3) return { category: 3, value: countArr[0].value };
  if (countArr[0]?.count === 2 && countArr[1]?.count === 2) {
    const highPair = Math.max(countArr[0].value, countArr[1].value);
    return { category: 2, value: highPair };
  }
  if (countArr[0]?.count === 2) return { category: 1, value: countArr[0].value };

  // ---- Straight / Flush ----
  const straightHigh = naturalStraightHigh(values);
  if (natFlushSuit) {
    const topFlushVal = Math.max(...values);
    return { category: 5, value: topFlushVal };
  }
  if (hasJoker) {
    const suit4 = jokerFlushSuit(suits);
    if (suit4) {
      const topFlushVal = Math.max(...values, cardRanks.indexOf("A"));
      return { category: 5, value: topFlushVal };
    }
  }
  if (straightHigh !== null) return { category: 4, value: straightHigh };
  if (hasJoker) {
    const jsHigh = jokerStraightHigh(values);
    if (jsHigh !== null) return { category: 4, value: jsHigh };
  }

  // ---- High Card ----
  const highCard = Math.max(...values, hasJoker ? cardRanks.indexOf("A") : -1);
  return { category: 0, value: highCard };
}

function isRoyalHigh(highIdx) {
  return highIdx === cardRanks.indexOf("A");
}

function countSuits(suits) {
  const m = {};
  for (const s of suits) m[s] = (m[s] || 0) + 1;
  return m;
}

// Final Pai Gow validation
function isValidPaiGowSplit(lowHand, highHand) {
  const low = evaluateLowHand(lowHand);
  const high = evaluateHighHand(highHand);

  if (high.category < low.category) return false;
  if (high.category === low.category && high.value <= low.value) return false;
  return true;
}

export default isValidPaiGowSplit;
// export { evaluateLowHand, evaluateHighHand };
